import React, { useEffect, useRef, useState } from 'react';
import useWindowSize from '@hooks/use-window-size';
import clsx from 'clsx';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import useSalesQuery from '@hooks/orders-sales/use-sales-query';
import useUser from '@hooks/user/use-user';
import { Product } from '@interfaces/models/orderSaleProduct';
import { useContextSelector } from 'use-context-selector';
import { SearchContext } from '@context/search.context';
import ErrorView from '../common-views/error';
import { DEFAULT_FIRST_SEARCH_PAGE } from '../filters/search/search';
import { filterProducts, getOrderSaleURLQueries, saleListingId } from '../helper';
import { State } from '../type';
import styles from '../products.module.scss';
import ProductsView from '../product/product';
import Filters, { FilterType } from '../filters/filters';
import ProductWrapper from '../product/product-wrapper';

const SalesView: React.FC<{
  state: State;
  updateState: (s: State) => void;
}> = ({ state, updateState }) => {
  const { user } = useUser();

  const { sendAnalyticEvent } = useAnalyticEvents('orders_sales_timeline');

  const { shippingProduct, currentTab } = state;

  const [salesState, updateSaleState] = useState<{
    saleReport: string | null;
    search: string;
  }>({ saleReport: null, search: '' });

  const { isWiderThanMd } = useWindowSize();

  const [products, setProducts] = useState<Product[]>();

  const [showFilter, setShowFilter] = useState(false);
  const [filterType, setFilterType] = useState<FilterType>('search');

  const [submittedSearchText, setSubmittedSearchText] = useState<string>('');

  const [isSearchFetching, setSearchFetching] = useState(false);

  const [isSearchEmpty, setSearchEmpty] = useState(false);

  const [searchedProductIds, setSearchedProductIds] = useState<number[]>();

  const [currentSearchPage, setCurrentSearchPage] = useState(DEFAULT_FIRST_SEARCH_PAGE);

  const [hasMoreSearchResult, setHasMoreSearch] = useState(false);

  const [date, setDate] = useState<{ fromDate: string; toDate: string }>();

  /* For search case. As a flag for clicking View More */
  const isViewMoreRef = useRef(false);

  const isResetFilterRef = useRef(false);

  const searchQuery = useContextSelector(SearchContext, (v) => v.searchQuery);
  const setSearchQuery = useContextSelector(SearchContext, (v) => v.setSearchQuery);

  const enabled = !isSearchFetching && !isSearchEmpty;

  const { productId: bookmarkProductId } = getOrderSaleURLQueries();

  const {
    hasNextPage,
    isLoading,
    isFetching,
    isFetchingNextPage: isFetchingNextProductPage,
    fetchNextPage,
    isError,
    data,
  } = useSalesQuery(searchedProductIds, submittedSearchText, date?.fromDate, date?.toDate, bookmarkProductId, enabled);

  const saleReports = data && data.pages.length ? data.pages[0].saleReports : [];

  const hasSearch = !!submittedSearchText;

  const isFetchingProducts = isFetching || isSearchFetching;

  const isFetchingNextSearchPage = hasSearch && isViewMoreRef.current && isFetching;

  const isFetchingNextPage = isFetchingNextProductPage || isFetchingNextSearchPage;

  const resetSearching = () => {
    setSearchedProductIds(undefined);
    setSubmittedSearchText('');
    setSearchEmpty(false);
    setCurrentSearchPage(DEFAULT_FIRST_SEARCH_PAGE);
    isViewMoreRef.current = false;
    isResetFilterRef.current = true;
  };

  const resetDate = () => {
    setDate(undefined);
    isViewMoreRef.current = false;
    isResetFilterRef.current = true;
  };

  useEffect(() => {
    setShowFilter(user && user.segment === 'B2C');
  }, [user]);

  /* Handle clear search text */
  useEffect(() => {
    if (!searchQuery) {
      resetSearching();
    }
  }, [searchQuery]);

  /* Push infinite query data into products state */
  useEffect(() => {
    if (isFetching || !data) {
      return;
    }
    if (data.pages.length && !data.pages[0].products?.length) {
      setProducts(undefined);
      return;
    }

    const allProducts = data.pages
      .map((p) => p.products)
      .reduce((result, currentProducts, currentIdx) => {
        if (currentProducts.length) {
          const pds =
            currentIdx && bookmarkProductId
              ? currentProducts.filter((p) => p.productId !== bookmarkProductId)
              : currentProducts;
          result.push(...pds);
        }
        return result;
      }, []);

    /* Reflect isLoading of React-query, turn false when data is fetched */
    if (isResetFilterRef.current) {
      isResetFilterRef.current = false;
    }
    if (!hasSearch) {
      setProducts(allProducts);
      return;
    }

    if (isViewMoreRef.current) {
      const sp = products?.length ? [...products, ...allProducts] : [...allProducts];
      setProducts(sp);
    } else {
      setProducts(allProducts);
    }
  }, [data, isFetching, hasSearch, isWiderThanMd, bookmarkProductId]);

  /* 
    . Handle show selected product in the Product View.
    . Hook should be called only once at the first api called
    . isLoading: won't be triggered when fetchNextPage (use isLoading over isFetching)
    . isViewMoreRef: for search case, preventing Product View load and change to products[0] when click View More,
                     this is because useSalesQuery (so is isLoading) is called on every search.
    . isResetFilterRef: for triggering this hook one more time when reset the filter, cause isLoading won't trigger the hook at this point.
  */
  useEffect(() => {
    if (isSearchEmpty) {
      updateState({
        ...state,
        saleProduct: null,
        loading: false,
      });
      return;
    }
    if (isLoading || isResetFilterRef.current) {
      updateState({
        ...state,
        loading: (isLoading || isResetFilterRef.current) && !isViewMoreRef.current,
      });
      return;
    }

    if (isViewMoreRef.current) {
      isViewMoreRef.current = false;
      return;
    }

    let selectedProduct: Product | null = null;

    if (data && data.pages.length && data.pages[0].products?.length && isWiderThanMd) {
      selectedProduct = shippingProduct || data.pages[0].products[0];
    }

    updateState({
      ...state,
      saleProduct: selectedProduct,
      shippingProduct: shippingProduct,
      loading: false,
    });
  }, [isWiderThanMd, isLoading, shippingProduct, isResetFilterRef.current, isSearchEmpty]);

  useEffect(() => {
    if (shippingProduct && (hasSearch || !!date)) {
      if (hasSearch) {
        resetSearching();
        setSearchQuery('');
      }
      if (date) {
        resetDate();
      }
    }
  }, [shippingProduct, hasSearch, date]);

  /* Handle show Sales report */
  useEffect(() => {
    if (saleReports?.length && !salesState?.saleReport) {
      updateSaleState({
        ...salesState,
        saleReport: saleReports && saleReports.length > 0 ? saleReports[0].filePath : '',
      });
    }
  }, [saleReports, salesState?.saleReport]);

  /* Tracking search bar */
  useEffect(() => {
    if (!isFetching && !isSearchFetching) {
      const itemCount = data?.pages?.length ? data.pages[data.pages.length - 1].products?.length : 0;

      if (date) {
        sendAnalyticEvent('timeline_search', {
          property: `date_${date.fromDate}${date.toDate ? `_${date.toDate}` : ''}`,
          value: String(itemCount || 0),
        });
      } else if (hasSearch) {
        sendAnalyticEvent('timeline_search', {
          property: `keyword_${submittedSearchText.substring(0, 500)}`,
          value: String(itemCount || 0),
        });
      }
    }
  }, [data, isFetching, isSearchFetching]);

  if (isError) {
    return (
      <ErrorView
        currentTab={currentTab}
        forTab={'Sale'}
      />
    );
  }

  const onFilterChange = (value: FilterType) => {
    if (searchQuery) {
      resetSearching();
      setSearchQuery('');
    }
    if (date) {
      resetDate();
    }
    setFilterType(value);
  };

  const onLoadMore = () => {
    if (hasSearch) {
      isViewMoreRef.current = true;
      if (hasMoreSearchResult) {
        setCurrentSearchPage(currentSearchPage + 1);
      }
    } else {
      fetchNextPage();
      sendAnalyticEvent('timeline_load_more', { label: 'sales' });
    }
  };

  const negLeft = currentTab !== 'Sale' ? 'ordersSales__mainLeft__hasItems--neg' : '';

  return (
    <div className={clsx(styles.ordersSales__mainLeft__hasItems, styles[negLeft])}>
      {showFilter && (
        <Filters
          filterType={filterType}
          onChange={onFilterChange}
          currentPage={currentSearchPage}
          onProductIdChange={setSearchedProductIds}
          setMoreSearch={setHasMoreSearch}
          setSubmittedSearchText={setSubmittedSearchText}
          setSearchFetching={setSearchFetching}
          setSearchEmpty={setSearchEmpty}
          onDateChange={setDate}
        />
      )}

      <div
        className={styles.ordersSales__mainLeft__products}
        id={saleListingId}
      >
        <ProductWrapper
          currentTab={currentTab}
          isLoading={isFetchingProducts && !isFetchingNextPage}
          isFilter={!!hasSearch || !!date}
          hasProducts={!!products?.length}
          isSearchEmpty={isSearchEmpty}
        >
          <ProductsView
            state={state}
            updateState={updateState}
            order={null}
            products={filterProducts(products, shippingProduct)}
            hasNextPage={hasNextPage || hasMoreSearchResult}
            onLoadMore={onLoadMore}
            isFetchingNextPage={isFetchingNextPage || isFetchingNextSearchPage || isFetchingProducts}
          />
        </ProductWrapper>
      </div>
    </div>
  );
};

export default SalesView;
